import { pageTitle } from '@palaccount/config';
import type { JournalEntryResponse } from '@palaccount/contracts';
import { AlertCircle, CheckCircle2, Info, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { DocumentToolbar } from '@/components/document-toolbar';
import { ReasonBox } from '@/components/reason-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { useAccounts } from '@/features/accounts/api';
import { useCurrencyTable } from '@/features/fx/api';
import { localizeServerCause } from '@/lib/server-messages';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import {
  useApproveJournal,
  useCreateJournal,
  useDeleteJournal,
  useJournal,
  usePostJournal,
  useRejectJournal,
  useReverseJournal,
  useSubmitJournal,
  useUpdateJournal,
} from './api';
import {
  BLANK_LINE,
  computeBalance,
  deleteLine,
  toLineInputs,
  typeAmount,
  type EditorLine,
} from './journal-lines';
import { JournalSourceCell } from './journal-source';
import { JournalStatusChip } from './journals-list-page';

function toEditorLines(entry: JournalEntryResponse, baseCode: string): EditorLine[] {
  return entry.lines.map((line) => ({
    accountId: line.accountId,
    memo: line.memo ?? '',
    debit: line.debit === '0.00' ? '' : line.debit,
    credit: line.credit === '0.00' ? '' : line.credit,
    // The base is the empty option, so a base leg round-trips unchanged.
    currency: line.currency === baseCode ? '' : line.currency,
  }));
}

/**
 * FIN-02 — Journal Entry: the locked notice (one, in §7's priority), the
 * header details, the lines grid with the live balance strip (§5c), and the
 * approval workflow (submit → approve/reject → post → reverse). A draft —
 * pending and rejected included — edits freely and an edit returns it to
 * Draft (R8, Q-FIN-02-4); a posted entry is corrected only by Reverse.
 */
export function JournalEditorPage() {
  const { t, i18n } = useTranslation();
  const arabic = i18n.language.startsWith('ar');
  const navigate = useNavigate();
  const { entryId } = useParams<{ entryId: string }>();
  const isNew = entryId === undefined;

  const canCreate = useCan('accounting.create');
  const canEdit = useCan('accounting.edit');

  const { data: entry } = useJournal(entryId);
  const { data: accounts } = useAccounts();
  // FIN-05's table is what a line's currency may be (§11: the rate table
  // "feeds every screen that converts money").
  const { data: currencyTable } = useCurrencyTable();
  // GEN-107, from the session rather than a literal: the rate table is the
  // list of currencies a LINE may name; which one is the company's own comes
  // from `useBaseCurrency` (the same fact, resolved once for the whole app).
  const sessionBase = useBaseCurrency();
  const baseCode = currencyTable?.base.code ?? sessionBase;

  // N-16: the company's today, not the browser's — this date is period-checked
  // and posted, so a day out is a posting into the wrong month.
  const [entryDate, setEntryDate] = useDocumentDate();
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<EditorLine[]>([{ ...BLANK_LINE }, { ...BLANK_LINE }]);
  // The reason itself lives INSIDE `ReasonBox` now — it owns the field, clears
  // it on each opening, and hands the trimmed text to `onConfirm`.
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  // A23 — the third of this screen's asks. Like `rejecting`, it holds only
  // whether the question is open; the reason lives inside `ReasonBox`, which
  // owns the field and clears it on every opening.
  const [reversing, setReversing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('je.title'));
  }, [t]);

  // Load the fetched entry into the editor state.
  useEffect(() => {
    if (!entry) return;
    setEntryDate(entry.entryDate);
    setReference(entry.reference ?? '');
    setLines(entry.status === 'posted' || entry.status === 'reversed' || entry.autoGenerated
      ? toEditorLines(entry, baseCode)
      : [...toEditorLines(entry, baseCode), { ...BLANK_LINE }]);
  }, [entry, baseCode]);

  const createJournal = useCreateJournal();
  const updateJournal = useUpdateJournal(entryId ?? '');
  const deleteJournal = useDeleteJournal();
  const submitJournal = useSubmitJournal();
  const approveJournal = useApproveJournal();
  const rejectJournal = useRejectJournal();
  const postJournal = usePostJournal();
  const reverseJournal = useReverseJournal();

  // §6 "What locked covers": posted/reversed, or auto-generated in ANY state.
  const locked =
    entry !== undefined &&
    (entry.status === 'posted' || entry.status === 'reversed' || entry.autoGenerated);
  const editable = (isNew && canCreate) || (!locked && entry !== undefined && canEdit);

  // L1 — only postable, ACTIVE accounts are offered (BR-FIN-02-04, BR-FIN-01-09).
  const accountOptions = useMemo(
    () => (accounts ?? []).filter((account) => account.postable && account.active),
    [accounts],
  );
  const accountById = useMemo(() => new Map((accounts ?? []).map((a) => [a.id, a])), [accounts]);

  const balance = useMemo(() => computeBalance(lines, baseCode), [lines, baseCode]);

  /**
   * The cause reaches the decision. A refusal that arrived keeps the server's
   * sentence, localized — `JOURNAL_MESSAGES`' fixed sentences have had keys in
   * `STATIC_MESSAGE_KEYS` all along. A request that never came back gets this
   * screen's own sentence instead of the browser's `Failed to fetch`.
   * `je.errSaveFailed` is per-screen on purpose; see `localizeServerCause`.
   */
  const fail = (thrown: unknown) => setError(localizeServerCause(t, thrown, 'je.errSaveFailed'));

  const payload = () => ({
    entryDate,
    ...(reference.trim() !== '' ? { reference: reference.trim() } : {}),
    lines: toLineInputs(lines),
  });

  const save = async () => {
    setError(null);
    const body = payload();
    if (body.lines.length === 0) {
      setError(t('je.errNoLines'));
      return;
    }
    try {
      if (isNew) {
        const made = await createJournal.mutateAsync(body);
        navigate(`/accounting/journals/${made.id}`, { replace: true });
      } else {
        await updateJournal.mutateAsync(body);
      }
    } catch (thrown) {
      fail(thrown);
    }
  };

  const act = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (thrown) {
      fail(thrown);
    }
  };

  const remove = async () => {
    setDeleting(false);
    if (!entryId) return;
    await act(async () => {
      await deleteJournal.mutateAsync(entryId);
      navigate('/accounting/journals');
    });
  };

  /**
   * A23 (DELEGATED RULINGS, 26 Aug 2026) — "journal Reverse → asks, with a
   * typed reason, joining both vouchers."
   *
   * The `ReasonBox` IS the confirm, exactly as it is on the receipt and the
   * payment: the reason is required, so Escape, a click outside or an empty
   * box abandons the act and sends nothing. This function is reached only
   * from the dialog's own verb — the button now opens the question and does
   * nothing else (GEN-208).
   *
   * Only the ASK is new. The navigation to the reversing entry, the error
   * handling and the reversal itself are untouched.
   */
  const reverse = async (reason: string) => {
    if (!entryId) return;
    setReversing(false);
    await act(async () => {
      const reversal = await reverseJournal.mutateAsync({ entryId, reason });
      navigate(`/accounting/journals/${reversal.id}`);
    });
  };

  const status = entry?.status ?? 'draft';
  const postable = status === 'approved' && balance.balanced;

  // §7 — exactly one notice, in this priority.
  const notice =
    status === 'reversed'
      ? t('je.lockedReversing')
      : status === 'posted'
        ? t('je.lockedPosted')
        : entry?.autoGenerated
          ? t('je.lockedAuto')
          : null;

  return (
    <div className="grid gap-4">
      <AccountingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('je.title')}</h2>
        <Button size="sm" variant="outline" asChild>
          <Link to="/accounting/journals">{t('common.cancel')}</Link>
        </Button>
      </div>

      {notice ? (
        <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div>
            <p>{notice}</p>
            {/* A23 — the stored reason, read back OFF THE RECORD, in the
                §7 notice block: this is the journal's analogue of the voucher
                reversed-banner, which is where `receipts.reversedReason` and
                `pay.reversedReason` are shown. It renders on BOTH halves of
                the pair, because the reader most likely to be asking why an
                entry was undone is the one who opened the ORIGINAL.

                The null test is the point, not defensive noise: every entry
                reversed before 20260911100000 has no reason, exactly as a
                bounce-stamped voucher does, and "Reason: " with nothing after
                it is worse than silence. */}
            {entry?.reversalReason != null ? (
              <p className="mt-1 opacity-90">
                {t('je.reversedReason', { reason: entry.reversalReason })}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {/* ── The header details ──────────────────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-3 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1">
            <Label>{t('je.colNo')}</Label>
            <div className="flex h-12 items-center gap-2">
              <Ltr
                value={entry ? entry.code : t('je.codeNew')}
                className="font-mono text-sm"
              />
              {entry ? <span className="text-xs text-muted-foreground">· auto</span> : null}
            </div>
          </div>
          <div className="grid gap-1">
            <Label htmlFor="je-date">{t('common.date')}</Label>
            <Input
              id="je-date"
              type="date"
              value={entryDate}
              disabled={!editable}
              onChange={(e) => setEntryDate(e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>{t('je.colStatus')}</Label>
            <div className="flex h-12 items-center">
              <JournalStatusChip status={status} />
            </div>
          </div>
          <div className="grid gap-1">
            <Label>{t('je.fSource')}</Label>
            {/*
             * BR-FIN-02-12 — "Every automatic entry must carry a working link
             * to the document that produced it" — BUILT, under delegated
             * ruling A15 ("the source-column shape"). This is FIN-02 §5a
             * field 6, and the cell is `JournalSourceCell` so that the
             * register one click away says the identical thing.
             *
             * The `reversalOf` branch that used to live here is GONE rather
             * than kept beside it, and that is the point of the shape: a
             * reversal's source IS the entry it reverses (§5a names it as one
             * of the four kinds), so the ledger now stamps it like every
             * other door and the cell renders it with the same sentence this
             * branch used to — `je.reversalOfLink`, moved into the cell. One
             * fact, one path through the code, and no branch reading "…unless
             * it is a reversal".
             *
             * `reversedBy` STAYS, because it is the opposite direction and not
             * a source at all: it says what undid THIS entry, which is a fact
             * about the entry's future rather than its origin.
             */}
            <div className="flex h-12 items-center text-sm">
              {entry ? (
                <JournalSourceCell source={entry.source} autoGenerated={entry.autoGenerated} />
              ) : (
                <span className="rounded border border-dashed px-2 py-1 text-xs text-muted-foreground">
                  {t('je.typeManual')}
                </span>
              )}
              {entry?.reversedBy ? (
                <Link
                  to={`/accounting/journals/${entry.reversedBy.id}`}
                  className="ms-3 text-primary underline-offset-2 hover:underline"
                >
                  {t('je.reversedByLink', { no: entry.reversedBy.code })}
                </Link>
              ) : null}
            </div>
          </div>
          <div className="grid gap-1 sm:col-span-2 lg:col-span-4">
            <Label htmlFor="je-reference">{t('je.fReference')}</Label>
            <Input
              id="je-reference"
              placeholder={t('je.referencePh')}
              value={reference}
              disabled={!editable}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Journal lines (§5b) + the totals strip (§5c) ────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('je.linesTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2">
          <div className="grid grid-cols-[minmax(10rem,2fr)_minmax(7rem,2fr)_6rem_9rem_9rem_2.5rem] items-center gap-px text-xs font-medium text-muted-foreground">
            <div>{t('je.colAccount')}</div>
            <div>{t('sales.colDescription')}</div>
            <div>{t('je.colCurrency')}</div>
            <div className="text-end">{t('je.colDebit')}</div>
            <div className="text-end">{t('je.colCredit')}</div>
            <div />
          </div>
          {lines.map((line, index) => {
            const account = line.accountId !== null ? accountById.get(line.accountId) : undefined;
            return (
              <div
                key={index}
                className="grid grid-cols-[minmax(10rem,2fr)_minmax(7rem,2fr)_6rem_9rem_9rem_2.5rem] items-center gap-px odd:bg-muted/40"
              >
                {editable ? (
                  <NativeSelect
                    aria-label={`${t('je.colAccount')} ${index + 1}`}
                    className="h-10 text-sm"
                    value={line.accountId ?? ''}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((l, i) =>
                          i === index ? { ...l, accountId: e.target.value || null } : l,
                        ),
                      )
                    }
                  >
                    <option value="">{t('je.selectAccount')}</option>
                    {accountOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.code} {arabic ? option.nameAr : option.name}
                      </option>
                    ))}
                  </NativeSelect>
                ) : (
                  <div className="px-2 text-sm">
                    {account ? (
                      <>
                        <Ltr value={account.code} className="me-1 font-mono text-xs" />
                        {arabic ? account.nameAr : account.name}
                      </>
                    ) : (
                      '—'
                    )}
                  </div>
                )}
                <Input
                  aria-label={`${t('sales.colDescription')} ${index + 1}`}
                  className="h-10 text-sm"
                  placeholder={t('je.lineMemoPh')}
                  value={line.memo}
                  disabled={!editable}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l, i) => (i === index ? { ...l, memo: e.target.value } : l)),
                    )
                  }
                />
                {/* Per-line currency (BR-FIN-02-01): the balance rule holds
                    per currency, and this is how a foreign balance is booked
                    so FIN-05's revaluation has an FC leg to measure. */}
                <NativeSelect
                  aria-label={`${t('je.colCurrency')} ${index + 1}`}
                  className="h-10 text-sm"
                  value={line.currency}
                  disabled={!editable}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l, i) => (i === index ? { ...l, currency: e.target.value } : l)),
                    )
                  }
                >
                  <option value="">{baseCode}</option>
                  {(currencyTable?.rows ?? []).map((row) => (
                    <option key={row.id} value={row.code}>
                      {row.code}
                    </option>
                  ))}
                </NativeSelect>
                <Input
                  aria-label={`${t('je.colDebit')} ${index + 1}`}
                  dir="ltr"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="h-10 text-end font-mono text-sm"
                  value={line.debit}
                  disabled={!editable}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l, i) => (i === index ? typeAmount(l, 'debit', e.target.value) : l)),
                    )
                  }
                />
                <Input
                  aria-label={`${t('je.colCredit')} ${index + 1}`}
                  dir="ltr"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="h-10 text-end font-mono text-sm"
                  value={line.credit}
                  disabled={!editable}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((l, i) =>
                        i === index ? typeAmount(l, 'credit', e.target.value) : l,
                      ),
                    )
                  }
                />
                {editable ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('je.removeLine')}
                    title={t('je.removeLine')}
                    onClick={() => setLines((prev) => deleteLine(prev, index))}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
          {editable ? (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLines((prev) => [...prev, { ...BLANK_LINE }])}
              >
                <Plus className="me-1 size-4" aria-hidden />
                {t('je.addLine')}
              </Button>
            </div>
          ) : null}

          {/* The totals strip shares the grid so the columns line up. */}
          <div
            className={cn(
              'grid grid-cols-[minmax(10rem,2fr)_minmax(7rem,2fr)_6rem_9rem_9rem_2.5rem] items-center gap-px border-t-2 pt-2',
              balance.balanced ? 'border-emerald-500' : 'border-red-400',
            )}
          >
            <div className="col-span-2 flex items-center gap-2 text-sm font-medium">
              {balance.balanced ? (
                <>
                  <CheckCircle2 className="size-4 text-emerald-700 dark:text-emerald-400" aria-hidden />
                  <span className="text-emerald-700 dark:text-emerald-400">{t('je.balanced')}</span>
                </>
              ) : (
                <>
                  <AlertCircle className="size-4 text-red-600 dark:text-red-400" aria-hidden />
                  <span className="text-red-700 dark:text-red-400">
                    {t('je.outOfBalance')} · <Amount value={balance.difference} />
                  </span>
                </>
              )}
            </div>
            <div className="text-end font-mono text-sm font-bold">
              <Amount value={balance.totalDebit} />
            </div>
            <div className="text-end font-mono text-sm font-bold">
              <Amount value={balance.totalCredit} />
            </div>
            <div />
          </div>
        </CardContent>
      </Card>

      {/* ── Approval workflow (§5d/§6) ──────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('je.approvalTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          {entry?.submittedBy ? (
            <p className="text-sm text-muted-foreground">
              {t('je.submittedBy', { email: entry.submittedBy.email })}
            </p>
          ) : null}
          {entry?.approvedBy && entry.approvedAt ? (
            <p className="inline-flex w-fit items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
              <CheckCircle2 className="size-3.5" aria-hidden />
              {t('je.approvedChip', { email: entry.approvedBy.email, date: entry.approvedAt })}
            </p>
          ) : null}
          {status === 'rejected' && entry?.rejectionReason ? (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              {entry.rejectionReason}
            </div>
          ) : null}

          {/* THE SEVEN ACTS, DECLARED — delegated ruling D51 (27 Aug 2026):
              "The journal editor's acts move into DocumentToolbar's single
              row — the two-cluster approval Card was the hand-written
              screen's shape, not a spec demand." The <span className="me-auto"/>
              spacer that split them into two clusters is the one thing this
              move removes; every act, guard, gate and dialog is byte-equal.

              THE TOOLBAR STAYS INSIDE THE APPROVAL CARD, and that is a spec
              reading rather than a taste: FIN-02 §6's "Where it is" column
              puts Submit, Approve, Reject, Post and Reverse in the "Approval
              row" — moving them to the page header would contradict five
              cells of that table. What §6 pins WITHIN the row is only the
              trailing edge ("Post entry … Approval row, trailing edge";
              "Reverse entry" the same), which declaration order preserves:
              Post and Reverse are the last two acts drawn. No spec sentence
              pins the two-cluster split — D51's stop-condition was checked
              against §4, §6 and §8 before this was written. */}
          <DocumentToolbar
            actions={[
              {
                id: 'save',
                row: 'save',
                label: t('common.save'),
                size: 'sm',
                visible: !locked,
                gate: entry
                  ? { permission: 'accounting.edit', reasonKey: 'perm.cannot.editJournals' }
                  : { permission: 'accounting.create', reasonKey: 'perm.cannot.createJournals' },
                // This Save never had a spinner, and GEN-60 puts the spinner
                // and the disabling together or not at all — so it declares
                // `disabled`, not `busy` (the migrated fourteen's convention).
                disabled: createJournal.isPending || updateJournal.isPending,
                onAct: () => void save(),
              },
              {
                // Submit — a MANUAL draft only (§6). PART 4.6 has no Submit
                // row, so `row: null` is a statement, not an omission.
                id: 'submit',
                row: null,
                label: t('je.submit'),
                variant: 'outline',
                size: 'sm',
                visible:
                  entry !== undefined &&
                  !entry.autoGenerated &&
                  (status === 'draft' || status === 'rejected'),
                gate: { permission: 'accounting.edit', reasonKey: 'perm.cannot.submitJournals' },
                onAct: () => {
                  if (entry) void act(() => submitJournal.mutateAsync(entry.id));
                },
              },
              {
                /* Approve and Reject — pending only, greyed unless balanced
                   (§6). BOTH ASK FIRST, and that is GEN-209 rather than a
                   preference: its list (spec line 4709) names "approving or
                   rejecting a submission" verbatim. Approve asked NOTHING
                   before the decision-asks change; Reject asked in a bespoke
                   inline <Textarea>, the "new pattern" BR-PERM-15 forbids.
                   So Approve takes §16.5.3's shared ConfirmBox and Reject its
                   typed-reason sibling — both dialogs below, under their own
                   ternary. Neither is a segmented status selector: these are
                   BUTTONS THAT ACT, so the payment trio's "not a toolbar act"
                   verdict does not reach them (document-toolbar.tsx says the
                   same from the other side).

                   PART 4.6 has no Approve row, so `row: null`. */
                id: 'approve',
                row: null,
                label: t('je.approve'),
                size: 'sm',
                visible: entry !== undefined && status === 'pending_approval',
                gate: { permission: 'accounting.approve', reasonKey: 'perm.cannot.decideJournals' },
                // An UNBALANCED entry cannot be approved by anybody — a rule,
                // live-and-disabled with the hint beneath (16.7 row 3).
                //
                // ERR-FIN-02-03 — "clicked Approve on an unbalanced entry:
                // Approve is greyed" — is this attribute. The WORDING differs
                // on the styling only: the §14 row says "greyed at 40%
                // opacity", a prototype measurement, while this is the shared
                // <Button disabled>, whose greying is the design system's and
                // not a number any screen types. The row's remedy sentence
                // ("fix the balance first") is the hint below at
                // `je.hintUnbalanced` — "Debits must equal credits before
                // posting."
                //
                // The server is the wall, not this: `assertBalanced` runs
                // again at approval (`journals.service.ts`, BR-FIN-02-06), so
                // deleting this line greys nothing and refuses anyway. That is
                // also why the guard for this is written against the REQUEST
                // rather than the DOM — a disabled button and an absent button
                // look identical to a truthiness check.
                disabled: !balance.balanced,
                // GEN-208: the click OPENS the question and does nothing else.
                // The write happens on the dialog's own verb.
                onAct: () => setApproving(true),
              },
              {
                id: 'reject',
                row: null,
                label: t('je.reject'),
                variant: 'outline',
                size: 'sm',
                visible: entry !== undefined && status === 'pending_approval',
                gate: { permission: 'accounting.approve', reasonKey: 'perm.cannot.decideJournals' },
                onAct: () => setRejecting(true),
              },
              {
                // The document's question and the role's, kept apart (16.7).
                // `locked` is posted OR reversed OR auto-generated — wider
                // than the other editors' on purpose (BR-FIN-02-08a: "a DRAFT
                // entry, including one that is pending approval or has been
                // rejected, must be freely deletable"). This Delete is drawn
                // `outline`, exactly as the hand-written row drew it — the one
                // editor whose Delete already matches PART 4.6's "White,
                // outlined" cell.
                id: 'delete',
                row: 'delete',
                label: t('common.delete'),
                variant: 'outline',
                size: 'sm',
                visible: entry !== undefined && !locked,
                gate: { permission: 'accounting.delete', reasonKey: 'perm.cannot.deleteJournals' },
                onAct: () => setDeleting(true),
              },
              {
                // Post — greyed unless approved and balanced (§6), at the
                // trailing edge exactly where §6 pins it. `row: null` for the
                // receipt editor's reason one act over: PART 4.6's Confirm row
                // describes what posting does, but the table names buttons by
                // the word printed on them, and "Post entry" is not a word in
                // the table — claiming its row would invent a guarantee
                // rather than record one.
                id: 'post',
                row: null,
                label: t('je.postEntry'),
                size: 'sm',
                visible: entry !== undefined && !locked && !entry.autoGenerated,
                gate: { permission: 'accounting.approve', reasonKey: 'perm.cannot.postJournals' },
                disabled: !postable || postJournal.isPending,
                onAct: () => {
                  if (entry) void act(() => postJournal.mutateAsync(entry.id));
                },
              },
              {
                /* Reverse — posted only (§6, BR-FIN-02-10), and A23: it ASKS.
                   The click opens the question and does nothing else
                   (GEN-208); the write happens on the dialog's own verb.

                   AND ONCE ONLY (FIN-02 §15). `status === 'posted'` alone
                   cannot express that: owner ruling R8 keeps the ORIGINAL at
                   'posted' after it has been reversed — `reverse.ts`'s own
                   sentence is "THE ORIGINAL IS NEVER MUTATED" — so a reversed
                   entry sat here for ever still offering the act, and the
                   server refused it with `alreadyReversed` (409) every time.
                   `reversedBy` is the wire's statement that the one reversal
                   already exists, and it is the same field the §7 notice
                   block above draws its link from, so the guard and the
                   evidence the reader sees are one fact.

                   This is `receipt-editor-page.tsx`'s shape — `visible:
                   status === 'posted' && !receipt?.reversedAt` — and the
                   voucher's is why this is a screen guard rather than a
                   server one: the server was already right in three places.
                   16.7's rule for a control a user may not use is that it is
                   not drawn. */
                id: 'reverse',
                row: null,
                label: t('je.reverseEntry'),
                variant: 'outline',
                size: 'sm',
                visible: entry !== undefined && status === 'posted' && entry.reversedBy === null,
                gate: { permission: 'accounting.approve', reasonKey: 'perm.cannot.reverseJournals' },
                onAct: () => setReversing(true),
              },
            ]}
          >
            {/* THE DIALOGS KEEP THEIR TERNARIES — the fourteen-screen
                convention the roster entry recorded: the buttons moved to
                `visible:`, each dialog stays inside the guard that justifies
                it, so no box can outlive the state that allows its act.
                `je.approve` / `je.reject` are reused as the action labels
                rather than new keys: GEN-206 wants the VERB and both already
                are one (the `counts.post` precedent). NEITHER decision body
                carries "This cannot be undone." — editing an approved entry
                CLEARS the approval (journals.service.ts, Q-FIN-02-4) and a
                rejected entry can be resubmitted, and
                `journal-decision-asks.test.tsx` asserts the clause's absence
                in both languages so a false sentence cannot reach a user. */}
            {entry && status === 'pending_approval' ? (
              <>
                <ConfirmBox
                  open={approving}
                  title={t('je.confirmApproveTitle')}
                  body={t('je.confirmApproveBody')}
                  confirmLabel={t('je.approve')}
                  busy={approveJournal.isPending}
                  onConfirm={() => {
                    setApproving(false);
                    void act(() => approveJournal.mutateAsync(entry.id));
                  }}
                  onCancel={() => setApproving(false)}
                />
                <ReasonBox
                  open={rejecting}
                  title={t('je.confirmRejectTitle')}
                  prompt={t('je.confirmRejectBody')}
                  // Already the distinct-per-flow name `reason-box.tsx`
                  // demands — "Rejection reason", never a bare "Reason".
                  reasonLabel={t('je.rejectPrompt')}
                  confirmLabel={t('je.reject')}
                  busy={rejectJournal.isPending}
                  onConfirm={(reason) => {
                    setRejecting(false);
                    void act(() => rejectJournal.mutateAsync({ entryId: entry.id, reason }));
                  }}
                  onCancel={() => setRejecting(false)}
                />
              </>
            ) : null}
            {/* GEN-58 in the ONE shared box (GEN-204, GEN-209). Inside the
                same `!locked` guard as the button's `visible:`, so it cannot
                outlive the condition that allows the delete. */}
            {entry && !locked ? (
              <DeleteConfirmBox
                open={deleting}
                detail={t('je.confirmDelete')}
                onCancel={() => setDeleting(false)}
                onConfirm={() => void remove()}
                busy={deleteJournal.isPending}
              />
            ) : null}
            {/* Inside the same `posted` guard as the button's `visible:` —
                the convention the delete and the two decisions above set.
                `reversal.reasonLabel` and the prompt are the vouchers' own
                keys and frame; `reversal.confirmEntry` is the VERB,
                deliberately not the trigger's exact words, which is
                `reason-box.tsx`'s own rule. */}
            {entry && status === 'posted' && entry.reversedBy === null ? (
              <ReasonBox
                open={reversing}
                title={t('je.reverseEntry')}
                prompt={t('je.reversePrompt')}
                reasonLabel={t('reversal.reasonLabel')}
                confirmLabel={t('reversal.confirmEntry')}
                busy={reverseJournal.isPending}
                onConfirm={(reason) => void reverse(reason)}
                onCancel={() => setReversing(false)}
              />
            ) : null}
          </DocumentToolbar>

          {/* The post hint — hidden once posting is possible (§6). */}
          {entry && !locked && !postable ? (
            <p className="text-xs text-muted-foreground">
              {!balance.balanced ? t('je.hintUnbalanced') : t('je.hintNotApproved')}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
